import { z } from "zod";

/**
 * Error code emitted by `GET /worker-app/worker` when the caller's Cognito
 * identity (phone or email) maps to a self-restorable soft-deleted account.
 * Duplicated here (rather than imported from `@clipboard-health/contract-worker-app-bff`)
 * so we don't need to bump the contract package version just to read it.
 */
export const SOFT_DELETED_ACCOUNT_FOUND_ERROR_CODE = "SOFT_DELETED_ACCOUNT_FOUND";

/**
 * Emitted by the BFF when a soft-deleted account exists for the caller's
 * identity but is not restorable (previousStage outside the allow-list, e.g.
 * RESTRICTED/DEACTIVATED). Terminal — the app shows a logout-only screen.
 */
export const SOFT_DELETED_ACCOUNT_NOT_RESTORABLE_ERROR_CODE = "SOFT_DELETED_ACCOUNT_NOT_RESTORABLE";

const softDeletedAccountErrorBodySchema = z.object({
  errors: z
    .array(
      z.object({
        code: z.literal(SOFT_DELETED_ACCOUNT_FOUND_ERROR_CODE),
        meta: z.object({
          matchedBy: z.enum(["email", "phone"]),
          maskedEmail: z.string(),
          maskedPhone: z.string(),
        }),
      })
    )
    .min(1),
});

const notRestorableAccountErrorBodySchema = z.object({
  errors: z
    .array(
      z.object({
        code: z.literal(SOFT_DELETED_ACCOUNT_NOT_RESTORABLE_ERROR_CODE),
      })
    )
    .min(1),
});

export interface SoftDeletedAccountMeta {
  matchedBy: "email" | "phone";
  maskedEmail: string;
  maskedPhone: string;
}

export class SoftDeletedAccountError extends Error {
  readonly matchedBy: SoftDeletedAccountMeta["matchedBy"];
  readonly maskedEmail: string;
  readonly maskedPhone: string;

  constructor(meta: SoftDeletedAccountMeta) {
    super("Account previously deleted — reactivation required");
    this.name = "SoftDeletedAccountError";
    this.matchedBy = meta.matchedBy;
    this.maskedEmail = meta.maskedEmail;
    this.maskedPhone = meta.maskedPhone;
  }
}

export function parseSoftDeletedAccountErrorFromResponseBody(
  body: unknown
): SoftDeletedAccountMeta | undefined {
  const parsed = softDeletedAccountErrorBodySchema.safeParse(body);
  if (!parsed.success) {
    return undefined;
  }

  return parsed.data.errors[0].meta;
}

export class NotRestorableSoftDeletedAccountError extends Error {
  constructor() {
    super("Account was deleted and cannot be reactivated");
    this.name = "NotRestorableSoftDeletedAccountError";
  }
}

export function isNotRestorableSoftDeletedAccountErrorBody(body: unknown): boolean {
  return notRestorableAccountErrorBodySchema.safeParse(body).success;
}
